import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCollection } from "@/hooks/useCollection";
import { STAGES } from "@/constants/crm";
import { PageHeader, StatCard, Panel, StatusBadge } from "@/components/crm/common";
import { DataTable } from "@/components/crm/DataTable";
import { FilterBar, applyFilters } from "@/components/crm/FilterBar";
import { FormDialog } from "@/components/crm/FormDialog";
import { FlowStepper } from "@/components/crm/FlowStepper";
import { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileBox } from "@/components/crm/Files";
import { WhatsAppButton, WhatsAppHistory } from "@/components/crm/WhatsApp";
import { ClosureCheck } from "@/components/crm/ClosureCheck";
import { useMoney } from "@/components/crm/FinanceLock";
import { PersonChip } from "@/components/crm/UserImage";

const sec = (name, label) => ({ type: "section", name, label });

export const PROJECT_FIELDS = [
  sec("_client", "Client details"),
  { name: "client", label: "Client / company name", required: true },
  { name: "client_contact_person", label: "Contact person" },
  { name: "client_designation", label: "Designation" },
  { name: "client_phone", label: "Phone number", placeholder: "+91 98765 43210" },
  { name: "client_whatsapp", label: "Client WhatsApp number", placeholder: "+91 98765 43210" },
  { name: "client_email", label: "Email", type: "email" },
  { name: "client_alt_contact", label: "Alternate contact (name & number)" },
  { name: "client_city", label: "City" },
  { name: "client_industry", label: "Industry / client type" },
  { name: "client_gst", label: "GST number" },
  { name: "lead_source", label: "Lead source", type: "select", options: ["Referral", "Existing client", "Website", "Social media", "Event / exhibition", "Cold call", "Other"] },
  { name: "client_billing_address", label: "Billing address", type: "textarea" },
  sec("_brief", "1 · New Brief"),
  { name: "name", label: "Project name", required: true },
  { name: "event_type", label: "Event type", placeholder: "Wedding, launch, conference…" },
  { name: "project_received_date", label: "Project receive date", type: "date" },
  { name: "venue", label: "Venue / location" },
  { name: "guest_count", label: "Expected guests", type: "number" },
  { name: "budget", label: "Estimated budget (₹)", type: "number" },
  { name: "objective", label: "Event objective" },
  { name: "brief", label: "Client brief / attachment links", type: "textarea" },
  sec("_pitch", "2 · Pitch"),
  { name: "internal_review_date", label: "Internal review date", type: "date" },
  { name: "pitch_date", label: "Pitch / presentation date", type: "date" },
  sec("_approval", "3 · Approval"),
  { name: "client_approval_date", label: "Client approval date", type: "date" },
  sec("_handover", "4 · Handover"),
  { name: "project_start_date", label: "Project start date", type: "date" },
  { name: "lead", label: "Project Lead" },
  { name: "cs_spoc", label: "CS SPOC" },
  sec("_execution", "5 · Execution"),
  { name: "internal_deadline", label: "Internal deadline", type: "date" },
  { name: "client_deadline", label: "Client deadline", type: "date" },
  { name: "execution_date", label: "Execution / setup date", type: "date" },
  { name: "event_date", label: "Event date", type: "date" },
  sec("_closure", "6 · Closure"),
  { name: "final_delivery_date", label: "Final delivery date", type: "date" },
  sec("_status", "Status"),
  { name: "stage", label: "Project stage", type: "select", options: STAGES, required: true },
  { name: "status", label: "Health", type: "select", options: ["On Track", "At Risk"] },
];

const SECTIONS = PROJECT_FIELDS.reduce((acc, f) => { if (f.type === "section") acc.push({ ...f, fields: [] }); else acc[acc.length - 1].fields.push(f); return acc; }, []);
const today = () => new Date().toISOString().slice(0, 10);

function DetailSection({ s, p, money, idx }) {
  const stageNo = parseInt(s.label, 10);
  const done = stageNo && idx >= stageNo - 1;
  const rows = s.fields.filter((f) => !f.required || f.name !== "name").filter((f) => f.type !== "textarea" && !["stage", "status", "name"].includes(f.name));
  if (!rows.length) return null;
  return (
    <div data-testid={`project-section-${s.name.slice(1)}`} className="mt-5">
      <div className={`mb-2 text-[11px] font-bold uppercase tracking-[0.14em] ${done ? "text-[#15966B]" : "text-slate-400"}`}>{s.label}</div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        {rows.map((f) => {
          const v = p[f.name];
          const late = f.type === "date" && v && v < today() && STAGES.indexOf(p.stage) < STAGES.length - 1 && ["internal_deadline", "client_deadline", "final_delivery_date"].includes(f.name);
          return (
            <div key={f.name}><dt className="text-[11px] uppercase tracking-wider text-slate-400">{f.label}</dt>
              <dd data-testid={`project-detail-${f.name}`} className={`mt-0.5 ${late ? "font-semibold text-rose-600" : "text-slate-800"}`}>{f.name === "budget" && v ? money(v) : v || "—"}{late && " · overdue"}</dd></div>
          );
        })}
      </dl>
    </div>
  );
}

const LINKS = { "New Brief": "/pitch", Pitch: "/pitch", Approval: "/assignments?stage=approvals", Handover: "/assignments?stage=handover", Execution: "/assignments?stage=execution", Closure: "/budget" };

function ProjectDetail({ p, onClose, onEdit, update, remove }) {
  const { money } = useMoney();
  const { can } = useAuth();
  const [waKey, setWaKey] = useState(0);
  if (!p) return null;
  const idx = STAGES.indexOf(p.stage);
  const edit = can("Projects", "edit");
  const canAdvance = edit && (STAGES[idx + 1] !== "Execution" || can("Handover", "approve"));
  return (
    <Sheet open={!!p} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="project-detail-sheet" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display text-xl">{p.name}</SheetTitle>{p.project_code && <div data-testid="project-code" className="font-mono text-xs font-bold text-[#C5A46D]">{p.project_code}</div>}</SheetHeader>
        <div className="text-sm text-slate-500 mt-1">{p.client} · {p.venue || "Venue TBC"}</div>
        <div className="mt-5"><FlowStepper steps={STAGES} current={idx} testid="project-flow" /></div>
        <div className="mt-4 flex items-center gap-2 text-sm"><span className="text-[11px] uppercase tracking-wider text-slate-400">Health</span><StatusBadge value={p.status} /></div>
        {SECTIONS.map((s) => <DetailSection key={s.name} s={s} p={p} money={money} idx={idx} />)}
        {p.client_billing_address && <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-600"><div className="text-[11px] uppercase tracking-wider text-slate-400">Billing address</div>{p.client_billing_address}</div>}
        {p.brief && <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-600">{p.brief}</div>}
        <div className="mt-4"><FileBox testid="project-brief-files" entityType="project" entityId={p.id} category="brief" title="Client brief & attachments" canUpload={edit || can("Pitch", "edit")} /></div>
        <div className="mt-4"><FileBox testid="project-chat-files" entityType="project" entityId={p.id} category="chat" title="Saved from chat" canUpload={edit} /></div>
        <div className="mt-4"><WhatsAppHistory projectId={p.id} refreshKey={waKey} /></div>
        {STAGES[idx + 1] === "Closure" && canAdvance && (
          <div className="mt-6"><ClosureCheck projectId={p.id} onClose={(o) => update(p.id, { stage: "Closure", ...(o ? { closure_override: true } : {}) }, "Moved to Closure")} /></div>
        )}
        <div className="flex flex-wrap gap-2 mt-6">
          {idx < STAGES.length - 1 && canAdvance && STAGES[idx + 1] !== "Closure" && (
            <Button data-testid="project-advance-stage-button" onClick={() => update(p.id, { stage: STAGES[idx + 1] }, `Moved to ${STAGES[idx + 1]}`)}>
              Move to {STAGES[idx + 1]}<ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {edit && <Button data-testid="project-edit-button" variant="outline" onClick={onEdit}>Edit</Button>}
          <WhatsAppButton testid="project-whatsapp-button" size="default" label="Update client on WhatsApp" phone={p.client_whatsapp || ""} projectId={p.id} entityId={p.id} context="Project update" onSent={() => setWaKey((k) => k + 1)}
            text={`Hello ${p.client || ""},\n\nQuick update on ${p.name}: we are currently at the ${p.stage} stage${p.event_date ? ` for the event on ${p.event_date}` : ""}.${p.client_deadline ? ` Next client deadline: ${p.client_deadline}.` : ""}\n\nRegards,\nZero Design Studio`} />
          <Button asChild variant="outline" data-testid="project-open-stage-page"><Link to={LINKS[p.stage]}>Open {p.stage} page</Link></Button>
          {edit && <Button data-testid="project-delete-button" variant="ghost" className="text-rose-600" onClick={() => { remove(p.id); onClose(); }}>Delete</Button>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Projects() {
  const { items, create, update, remove } = useCollection("projects");
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [lead, setLead] = useState("");
  const [form, setForm] = useState(null);
  const [selId, setSelId] = useState(null);
  useEffect(() => { if (params.get("new")) { setForm({ stage: "New Brief", status: "On Track" }); setParams({}); } }, [params, setParams]);
  useEffect(() => {
    if (params.get("q")) setSearch(params.get("q"));
    if (params.get("id")) setSelId(params.get("id"));
  }, [params]);
  const rows = applyFilters(items, search, ["name", "client"], [{ key: "stage", value: stage }, { key: "lead", value: lead }]);
  const sel = items.find((p) => p.id === selId);

  const save = async (f) => !!(f.id ? await update(f.id, f) : await create({ pitch_step: 0, pitch_checklist: {}, handover_checklist: {}, ...f }, "Project created"));

  return (
    <>
      <PageHeader num="02" title="Project Management" subtitle="Manage clients, pitches and confirmed events.">
        {can("Projects", "create") && <Button data-testid="create-project-button" onClick={() => setForm({ stage: "New Brief", status: "On Track" })}><Plus className="h-4 w-4 mr-1" />Create Project</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map((s) => <StatCard key={s} testid={`stage-count-${s.replace(/\s/g, "-").toLowerCase()}`} label={s} value={items.filter((p) => p.stage === s).length} />)}
      </div>
      <Panel title="Project register">
        <FilterBar testid="projects" search={search} setSearch={setSearch} filters={[
          { key: "stage", label: "stages", value: stage, set: setStage, options: STAGES },
          { key: "lead", label: "leads", value: lead, set: setLead, options: [...new Set(items.map((p) => p.lead).filter(Boolean))] },
        ]} />
        <DataTable testid="projects-table" rows={rows} onRowClick={(r) => setSelId(r.id)} columns={[
          { key: "name", label: "Project", render: (r) => <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-xs text-slate-500">{r.client}</div></div> },
          { key: "stage", label: "Stage", render: (r) => <StatusBadge value={r.stage} /> },
          { key: "status", label: "Health", render: (r) => <StatusBadge value={r.status} /> },
          { key: "event_date", label: "Event date" },
          { key: "lead", label: "Lead", render: (r) => r.lead ? <PersonChip name={r.lead} /> : "—" },
          { key: "cs_spoc", label: "CS SPOC" },
          { key: "client_deadline", label: "Client deadline" },
        ]} />
      </Panel>
      <ProjectDetail p={sel} onClose={() => setSelId(null)} onEdit={() => setForm(sel)} update={update} remove={remove} />
      <FormDialog testid="project-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit project" : "New project"} fields={PROJECT_FIELDS} initial={form} onSubmit={save} wide />
    </>
  );
}
